// A1 — SUPPORT TRIAGE, AS LAW.
//
// A scheduled pass reads new member messages, classifies each, and turns ONLY the unambiguously
// technical ones into DRAFT runbook orders for her one-tap approval. Everything about how a message
// is classified, what a draft looks like, what the member is told and what may be automated lives
// HERE, imported by both the server and the dashboard, and RUN by `scripts/a1-triage.mjs`.
//
// THE ONE SENTENCE THIS FILE EXISTS TO ENFORCE, because it is the whole safety design:
// **CLASSIFICATION IS ALLOWLIST-STRICT. Only unambiguous technical breakage is TECHNICAL; ANY DOUBT
// DEMOTES TO THE OWNER'S PILE.** Every default in here points the same way. A classifier that is
// wrong in the TECHNICAL direction drafts a runbook order about somebody's refund; a classifier that
// is wrong in the OWNER direction makes her read one more message. Those two mistakes are not the
// same size, and nothing in this file pretends they are.
//
// Imports nothing (the entry requirement for the gate to load and RUN it).

// ---------------------------------------------------------------------------
// THE FIVE PILES (A1-1; the fifth is her 2026-08-31 order ruling).
// ---------------------------------------------------------------------------
export const CATEGORIES = [
  {
    key: "technical",
    label: "Technical",
    note: "A feature or page is broken. The only class that may become a draft order.",
    auto: true,
    desk: true,
  },
  {
    key: "financial",
    label: "Financial",
    note: "Anything touching money: refunds, charges, tiers-as-billing.",
    auto: false,
    desk: false,
  },
  {
    key: "personal",
    label: "Account / personal",
    note: "Identity, disputes, distress, and ANYTHING ambiguous.",
    auto: false,
    desk: false,
  },
  // >>> **HER RULING, 2026-08-31: PRODUCT ORDERS ARE NOT ANSWERED ON THIS SITE AT ALL.** <<<
  //
  // Her words: *"Ignore any tickets regarding order issues because that is from my Previous company
  // and some people are using that as a way to go outside the process. No Order issues from The
  // Conjure are allowed to be answered on this site. I need the IT agent to redirect them to
  // orderissues@theconjure.com for any questions regarding product orders... And those do not become
  // IT tickets."*
  //
  // **`auto: false` IS THE HALF THAT MAKES THIS STRUCTURAL RATHER THAN A LABEL.** A ticket is filed
  // on the auto lane and nowhere else, so an order question can no longer become an IT ticket by any
  // path at all — it is not a filter somebody has to remember to apply.
  //
  // **AND `desk: false`, WHICH IS NOT THE SAME AS LEAVING HER UNANSWERED.** She IS answered — the
  // triage pass sends her `ORDER_REDIRECT` as her one acknowledgment, with the address and the plain
  // fact that this house cannot help. What `desk: false` withholds is the LIVE DESK composing an
  // answer of its own about a product order: a support brain that may speak freely here is one that
  // can end up discussing somebody's shipment on a site that has no access to any of it, which is
  // the going-outside-the-process her ruling is about.
  //
  // It also keeps `isDeskLane`'s biconditional whole: every lane `preClassify` can FORCE carries
  // `desk: false`, because a forced lane is one where being wrong is expensive.
  {
    key: "order",
    label: "Product order (The Conjure)",
    note: "A shipping, tracking or delivery question about a product order. NEVER answered here and never an IT ticket: redirected to orderissues@theconjure.com.",
    auto: false,
    desk: false,
  },
  { key: "other", label: "Other", note: "Everything else.", auto: false, desk: true },
];
export const CATEGORY_KEYS = CATEGORIES.map((c) => c.key);
export const categoryValid = (k) => CATEGORY_KEYS.includes(String(k || ""));
export const isAutoLane = (k) => Boolean(CATEGORIES.find((c) => c.key === k)?.auto);

// ITEM 4b / SD3 — **`desk` IS A SECOND FLAG BESIDE `auto` BECAUSE THEY ARE TWO QUESTIONS (R18), AND
// THIS ROUND SHIPPED THE WRONG ANSWER FIRST AND WAS CAUGHT BY DRIVING IT (R25/R129).**
//
// `auto` is *may the house draft a WORK ORDER for this lane* — TS1(3)(c)'s division, true of
// `technical` alone. SD3's is *may the desk TALK to this member at all*, which is a wider set: her
// clause (a) is **a how-do-I question or a lost member**, and those land in `other` by this roster's
// own note (*"everything else"*). The first draft derived `deskMayAnswer` from `isAutoLane`, and
// driven over the four lanes it routed **every how-do-I question to the owner** — SD3(a) deleted, on
// a desk whose whole job is answering them.
//
// **THE DIVISION IS `preClassify`'s, AND IT IS HELD AS A BICONDITIONAL RATHER THAN KEPT IN STEP:**
// the three vocabularies that FORCE a lane — distress, identity and money — are exactly the three
// the desk may never act on, which is her clause (c) plus the crisis laws. So a lane `preClassify`
// can force must carry `desk: false`, and a fifth category joins by declaring rather than by
// somebody remembering (R11/R31).
export const isDeskLane = (k) => Boolean(CATEGORIES.find((c) => c.key === k)?.desk);

// ---------------------------------------------------------------------------
// ITEM 9 (2026-08-07) — THE CONTACT FORM'S OWN LANE, AND WHY IT LIVES BESIDE THE FOUR PILES.
//
// The form asks the member what her message is ABOUT before anybody classifies it, and that answer
// is the only classification that exists at the moment she presses Send. Triage is a PASS that runs
// LATER, so at that moment nothing knows whether her message will become a ticket — and telling her
// "I have written this down and put it in today's list" before the classifier has run is a promise
// about a decision nobody has made yet (R18).
//
// So each reason DECLARES the pile it lands in, and the server's `CONTACT_REASONS` roster is DERIVED
// from this list rather than typed a second time (R28/R29): a reason the form offers that no pile
// claims is not a validation gap, it is a message with nowhere to go.
//
// **THE FILTER IS `isAutoLane`, NEVER A SECOND TECHNICAL ROSTER.** TS1(3)(c)'s division is what keeps
// money, ownership and deletion off her IT sheet — the sheet's own header says *"All of these need to
// be fixed"*, so a general question filed onto it is a work order about something that is not broken.
// That division is the one function directly above, and this asks it (R28/R29/R31).
// ---------------------------------------------------------------------------
export const CONTACT_LANES = [
  { reason: "Payment related", category: "financial" },
  { reason: "Technical support", category: "technical" },
  { reason: "General question", category: "other" },
];

/** What the form may offer. DERIVED, so the roster and the lanes cannot disagree. */
export const CONTACT_REASONS = CONTACT_LANES.map((l) => l.reason);

/**
 * The pile a reason lands in. An unknown reason names NO pile rather than a plausible one — this
 * house's standing ruling on an unrostered key (R25's DrawerTab), and here it is what makes the
 * filter below fail CLOSED.
 */
export const contactReasonCategory = (reason) =>
  CONTACT_LANES.find((l) => l.reason === String(reason || ""))?.category || "";

// >>> **AN ORDER QUESTION, NAMED BY ITS OWN NOUNS.** <<<
//
// Deliberately NOT a bare word-boundary "order": *"in order to"* appears in ordinary member writing,
// and a false positive here does not merely mislabel a message — **it tells a woman with a broken
// page to go email a company that cannot help her.** So it asks for shipping vocabulary, or for an
// order with a determiner in front of it.
// >>> **`never came` MAY NOT MATCH `never came BACK`, AND A REAL MEMBER PAID FOR THAT WORD (2026-09-03).**
// <<< c148 is Laura asking whether the CLOSET will let her add several oils at once, and she wrote
// *"I missed those when they were in stock. **They never came back.**"* — a question about this
// site's own stock, matching this lane on `never came`.
//
// **THE COST OF THAT MATCH IS TOTAL, WHICH IS WHY IT IS WORTH A LOOKAHEAD.** The order lane is
// `forced: true` and `desk: false` and `auto: false`, so a message that lands here is sent
// `ORDER_REDIRECT` — *"email orderissues@theconjure.com"* — **and files no ticket and reaches no
// desk.** A member asking about a feature of this house would be pushed out of it to an address that
// cannot help her, and her real question would reach nobody. That is #189's defect (a member sent
// somewhere useless) with the door closed behind her.
//
// **SHE ESCAPED IT ONLY BY DATE:** her message is from 2026-08-21 and this lane shipped on the 31st,
// so she was filed `other`. The next member to write *"they never came back"* about stock would not.
//
// `came back` is never how anybody describes a parcel — it means restocked, or a reply that returned
// — so the lookahead removes the false positive and takes no true one with it.
export const ORDER_RE = /\b(?:tracking(?:\s+(?:number|info|link|code))?|shipp(?:ed|ing)|shipment|package|parcel|usps|fedex|dhl|courier|(?:my|the|an|another|placed\s+(?:an|my))\s+order|order\s+(?:number|status|#|confirmation)|never\s+(?:arrived|shipped|came(?!\s+back))|(?:have\s?n.t|has\s?n.t|not)\s+(?:yet\s+)?receiv(?:ed|e)\s+(?:my|the|it|them))\b/i;

/**
 * **DOES THIS FORM SUBMISSION FILE A TICKET AT THE FORM?**
 *
 * Asked at BOTH ends — the form files when it is true, and the triage pass declines to file again
 * when it is true — so the two ends cannot disagree about which reasons file and land the same
 * broken page on her sheet twice under two sources.
 */
export function contactFilesTicket(reason) {
  return isAutoLane(contactReasonCategory(reason));
}

/**
 * >>> **IS THIS A PRODUCT ORDER QUESTION? (her ruling, 2026-08-31)** <<<
 *
 * **THE REASON SHE PICKS IS NOT ENOUGH, AND THAT IS HOW BOTH OF THESE REACHED HER SHEET.** The form
 * files a ticket off the REASON alone, so a member who picks "Technical support" and then writes
 * about a package that never arrived files an IT ticket about a product order — which is exactly
 * what her ruling forbids, and exactly how tickets #162 and #166 were raised.
 *
 * So the TEXT is asked too. It reads the same `ORDER_RE` the pre-pass reads, because *what an order
 * question is* may have one answer in this house and not two (R28/R29).
 */
export function isOrderQuestion(text) {
  return ORDER_RE.test(String(text ?? ""));
}

// ---------------------------------------------------------------------------
// THE DETERMINISTIC PRE-PASS. Runs BEFORE the engine and can only ever DEMOTE.
//
// Why a pre-pass at all when an engine call follows it: money and distress are the two classes where
// being wrong is expensive, and both are recognisable by vocabulary. If a message says "refund" it
// goes to her pile whatever a model thinks, and no engine opinion can promote it back. The engine is
// therefore only ever deciding between TECHNICAL and NOT-TECHNICAL for messages that already cleared
// this floor.
// ---------------------------------------------------------------------------
// ITEM 12 — **THESE THREE ARE EXPORTED, AND THAT IS A DERIVATION RATHER THAN A CONVENIENCE.** Lala's
// inbox answerer (`src/inboxAnswer.js`) has to make the same three refusals this pre-pass makes, and
// its order calls them "the crisis laws". A second copy of a distress regex is a second answer key
// to the one question on this site where being wrong is worst (R13's disease, and the same shape
// R31 closed by deriving instead of duplicating). `preClassify` below still reads exactly these, so
// there is one declaration and both readers are held to it.
export const MONEY_RE = /\b(?:refund(?:ed|ing)?|charge[ds]?|charging|overcharg|double[\s-]?charg|billed?|billing|invoice|receipt|payment|paid|card|bank|paypal|subscription price|cancel(?:l?ed|l?ing)? my (?:membership|subscription)|money back|dispute|chargeback|price|cost|\$\d)/i;

export const DISTRESS_RE = /\b(?:suicid|kill myself|end my life|self[\s-]?harm|hurt myself|abus(?:e|ed|ive)|threat(?:en|ened)?|harass(?:ed|ment)?|stalk(?:ed|er|ing)?|died|passed away|funeral|cancer|hospital|terminal|emergency|crisis)\b/i;

// Her 2026-10-17 ruling: a member asking for a copy of her readings or data is answered by a person on request
// (the Privacy Policy names the address); this routing is invisible to members.
export const IDENTITY_RE = /\b(?:delete my account|close my account|my data|(?:a |an )?cop(?:y|ies) of (?:all )?my (?:readings?|reports?|charts?|data|information|records?)|gdpr|ccpa|privacy|legal|lawyer|attorney|sue|lawsuit|police|identity theft|somebody else(?:'s)? account|not my account|hacked|someone (?:has|got) (?:my|into my) account)\b/i;

// Unambiguous breakage vocabulary. Necessary for TECHNICAL, never sufficient — the engine still has
// to agree, and the demotions above still outrank it.
//
// **EXPORTED FOR SD6, WHICH IS A DERIVATION RATHER THAN A CONVENIENCE (R28/R29/R31).** The Living
// Room redirect has to answer *does this post look like a support message*, and a second breakage
// regex would be a second answer to that — with the one that drifts deciding whether a member gets
// pointed anywhere. One declaration, both readers held to it, exactly as the three crisis regexes
// above are.
// >>> **THE `will not X` BRANCH WAS THREE VERBS SHORT, AND SD6 FOUND IT ON ITS FIRST DRIVE.** <<< It
// covered `will not load` and `will not work` and stopped there, so **`my seal will not download` —
// the single most typical sentence a member writes into the wrong room — matched NOTHING**, while
// `cannot download` two alternations along matched perfectly. The verbs are unified rather than a
// fourth near-copy added, which is what let three of them go missing in the first place.
//
// **THE DIRECTION IS SAFE AND IS ARGUED RATHER THAN ASSUMED (R19).** This vocabulary is NECESSARY
// for `technical` and never sufficient — `classify` also requires the engine to agree, to be
// confident, and the message not to be craft — so widening it can only REMOVE a veto on a message
// that really is describing something not working. It cannot promote anything on its own.
const NOT_WORKING = "(?:wo|will|does|did|do)\\s?n'?o?t\\s+(?:load|work|open|download|play|show|generate|save|let me)";
export const BREAKAGE_RE = new RegExp(`\\b(?:error|errors|erroring|crash(?:ed|es|ing)?|blank (?:page|screen)|white screen|not load(?:ing)?|${NOT_WORKING}|stuck (?:on|at)|spinner|spinning forever|404|500|broken|not work(?:ing)?|can'?t (?:open|download|play|see|access|log ?in|sign ?in)|cannot (?:open|download|play|see|access|log ?in|sign ?in)|fail(?:s|ed|ing)? to (?:load|open|download|generate|save)|no(?:t)? (?:showing|rendering|displaying|generating)|missing|disappeared|frozen|freezes)\\b`, "i");

// A message that is mostly a question about the craft is not a support ticket at all.
const CRAFT = /\b(?:what does .{0,40}mean|how do i (?:work|dress|read|pull)|which (?:candle|oil|herb|psalm|seal)|is it (?:okay|ok) to|should i|my (?:chart|reading|cards?|spread|numbers?) (?:says?|shows?)|new moon|full moon|mercury retrograde)\b/i;

// THE PRE-PASS. Returns a forced category, or null to let the engine decide.
// Nothing in here can force TECHNICAL. That is the asymmetry, made structural.

export function preClassify(text) {
  const s = String(text ?? "");
  if (!s.trim()) return { category: "other", why: "empty message", forced: true };
  if (DISTRESS_RE.test(s)) return { category: "personal", why: "distress or safety language", forced: true };
  if (IDENTITY_RE.test(s)) return { category: "personal", why: "identity, data, or legal language", forced: true };
  // **BEFORE MONEY, AND THAT ORDER IS THE RULING RATHER THAN A PREFERENCE.** An order question is
  // full of money words — paid, receipt, charged, price — so leaving it below MONEY_RE would file
  // every one of them as FINANCIAL and put them straight back into the process her ruling exists to
  // keep them out of. Safety and identity still outrank it: a woman in distress who happens to
  // mention a package is not an order question.
  if (ORDER_RE.test(s)) return { category: "order", why: "a product order, shipping or tracking question", forced: true };
  if (MONEY_RE.test(s)) return { category: "financial", why: "money language", forced: true };
  return null;
}

// Fold the engine's verdict in, under the pre-pass, with every tie-break pointing at her pile.
export function classify(text, verdict = null) {
  const forced = preClassify(text);
  if (forced) return { ...forced, by: "rules" };

  const guess = String(verdict?.category || "");
  const confident = verdict?.confident === true;
  const s = String(text ?? "");

  // TECHNICAL requires ALL THREE: the engine said so, the engine said it was sure, and the message
  // actually contains breakage vocabulary. Any one missing and it goes to her.
  if (guess === "technical" && confident && BREAKAGE_RE.test(s) && !CRAFT.test(s)) {
    return { category: "technical", why: verdict?.why || "unambiguous breakage", by: "engine", forced: false };
  }
  if (guess === "technical") {
    return {
      category: "personal",
      why: "read as technical but not unambiguous, so it demotes to your pile",
      by: "engine-demoted",
      forced: false,
    };
  }
  if (categoryValid(guess) && guess !== "technical") {
    return { category: guess, why: verdict?.why || "", by: "engine", forced: false };
  }
  // No usable verdict at all (engine down, malformed answer). SHE READS IT. A triage pass that
  // cannot reach its engine must not quietly file everything as "other" and look like it worked.
  return { category: "personal", why: "no classification available, so it goes to your pile", by: "unavailable", forced: true };
}

// ---------------------------------------------------------------------------
// A1-2 — THE SAFE LANE. A technical message becomes a DRAFT ORDER IN THE HOUSE FORMAT.
//
// Composed from the member's own words, deterministically. There is no second engine call to "write
// the order" — the house format is a shape, not prose, and a model asked to write a runbook order
// would paraphrase the member. **HER WORDS ARE QUOTED VERBATIM** (the sacred-words law), which is
// exactly what a paraphrase would destroy.
// ---------------------------------------------------------------------------
export const DRAFT_HEADER = "DRAFTED BY TRIAGE — NOT YET ORDERED";

export function composeDraft({ message, member = {}, id = "" } = {}) {
  const quoted = String(message ?? "").trim().split(/\r?\n/).map((l) => `##       > ${l}`).join("\n");
  const tier = member.tier ? `tier ${member.tier}` : "tier unknown";
  const device = member.device ? `, ${member.device}` : "";
  const ref = id ? ` (triage ${id})` : "";

  return [
    `## - [ ] **${DRAFT_HEADER}${ref}**`,
    "##",
    "##       HER WORDS, VERBATIM. Nothing below paraphrases them, and nothing may.",
    quoted,
    "##",
    `##       WHO: ${member.roomName || member.name || "a member"}, ${tier}${device}. Linked in the`,
    "##       Triage queue, so the fix is verified against HER account rather than a fresh one.",
    "##",
    "##       **TRACE FIRST, REPORT BEFORE REPAIR.** Follow the call from the surface she named to the",
    "##       route to the store to the vendor, and SAY WHICH LAYER IS WRONG before changing a line.",
    "##       A fix that starts at the symptom is how the same defect ships twice.",
    "##",
    "##       MEMBER DATA LAWS, RESTATED because this order touches a real account: accounts are",
    "##       islands; her readings, journal and conversations are hers and are never read to debug",
    "##       somebody else's problem; nothing member-facing changes without her seeing it.",
    "##",
    "##       DONE WHEN: the trace is written down, the cause is fixed rather than the symptom, a",
    "##       fixture holds it, and the surface she named is verified on production.",
  ].join("\n");
}

// A one-line summary for the queue list. Her words, trimmed, never rewritten.
export function draftTitle(message) {
  const one = String(message ?? "").replace(/\s+/g, " ").trim();
  return one.length > 90 ? `${one.slice(0, 88)}…` : one || "(no message)";
}

// ---------------------------------------------------------------------------
// A1-3 — TRAINING WHEELS. HER CONTROL, AND THE PHASE IS A STORED SETTING, NOT A CONSTANT.
// ---------------------------------------------------------------------------
export const PHASES = {
  1: { key: 1, label: "Every draft waits for you", autoQueue: false },
  2: { key: 2, label: "Approved-class technical drafts queue themselves", autoQueue: true },
};
export const DEFAULT_PHASE = 1;

// TS1(3)(c) — **NOTHING PAYMENT, ACCOUNT-OWNERSHIP OR DELETION CLASS IS EVER AGENT-ACTIONED.**
//
// Her clause names three classes and this house sorts into four piles, so the mapping is STATED
// rather than assumed: payment is `financial`, and account-ownership and deletion are both
// `personal` — `IDENTITY_RE` forces the second two by name ("delete my account", "close my account",
// "my data", "hacked", "not my account") and `MONEY_RE` the first, BEFORE the engine is ever asked,
// so no engine opinion can promote one of them into the lane an agent may touch.
//
// **IT IS DERIVED FROM `CATEGORIES` RATHER THAN A SECOND LIST, AND THAT IS THE REPAIR RATHER THAN
// THE RESTATEMENT (R31).** `mayAutoQueue` hard-typed `category !== "technical"` beside a roster whose
// `auto` column is the declaration of which lane is automatic — two answer keys for one fact, and the
// dangerous direction is the quiet one: setting `auto: false` on the technical row (a deliberate
// lockdown) would have closed the roster's door and left the queue's wide open. There is one
// predicate now, both doors ask it, and a fifth category arrives refused by EXISTING.
export function mayAgentAct({ category } = {}) {
  return isAutoLane(category);
}

// May this draft enter RUNBOOK.md without her tap? Phase 2 is HER WORD LATER, EARNED IN THE LEDGER,
// and even then it is TECHNICAL ONLY. **FINANCIAL AND PERSONAL NEVER AUTO-ANYTHING IN ANY PHASE** —
// that is the clause with no exception, so it is checked first and independently of the phase.
export function mayAutoQueue({ category, phase = DEFAULT_PHASE } = {}) {
  if (!mayAgentAct({ category })) return false;
  return Boolean(PHASES[phase]?.autoQueue);
}

export const DRAFT_ACTIONS = [
  { key: "approve", label: "Approve", note: "Enters RUNBOOK.md as a real order." },
  { key: "edit", label: "Edit", note: "Change the order, then approve it." },
  { key: "reject", label: "Reject", note: "Never becomes an order. Your note teaches the classifier." },
];
export const DRAFT_ACTION_KEYS = DRAFT_ACTIONS.map((a) => a.key);
export const draftActionValid = (k) => DRAFT_ACTION_KEYS.includes(String(k || ""));

// What a rejection teaches. Kept as structured data rather than prose so the next classifier round
// can read it: which category it was filed as, what she said it should have been, and her reason.
export function rejectionLesson({ category, correctedTo = "", note = "" } = {}) {
  return {
    was: category,
    shouldBe: categoryValid(correctedTo) ? correctedTo : null,
    note: String(note || "").slice(0, 500),
  };
}

// ---------------------------------------------------------------------------
// A1-4 — THE MEMBER'S SIDE. EXACTLY TWO AUTOMATED TOUCHES, EVER.
//
// FROM THE HOUSE, NOT FAKE-LALA. This is the line that matters most in the file: an automated note
// signed as Lala is the house putting words in her mouth, and the whole site is built on her voice
// being hers. So these say "we" and never "I", carry no name, and are deliberately plain.
// NO PROMISES OF TIMELINES. NO MACHINERY TALK.
// ---------------------------------------------------------------------------
export const MEMBER_TOUCHES = ["acknowledgment", "closure"];
export const MAX_MEMBER_TOUCHES = 2;

export const ACKNOWLEDGMENT =
  "We have your message and somebody is looking at it. We will not leave you guessing: when there is something real to tell you, you will hear from us here.";

// >>> **WHAT AN ORDER QUESTION IS ANSWERED WITH (her ruling, 2026-08-31).** <<<
//
// One message that answers her whole question: the address, and the plain fact that this house
// cannot help with it. **It deliberately does NOT say somebody is looking into it** — which is
// exactly what the ordinary acknowledgment promises, and promising it here is how a member ends up
// waiting on a house that was never going to answer.
export const ORDER_REDIRECT =
  "Product orders are handled by The Conjure and not here, so we are not able to help with this one — and we do not want to leave you waiting on us. Please email orderissues@theconjure.com with your order details and they will take care of you.";

export const CLOSURE =
  "The thing you wrote to us about has been fixed. If it is still not right for you, write back and say so and we will pick it straight back up.";

// TS1(5) — WHAT SHE IS ASKED BEFORE THE CLOSURE GOES, AND WHY IT IS A LAW RATHER THAN A LITERAL.
//
// The closure is the ONE touch a human decides (`sendClosure`'s own comment: *only she knows a fix
// is really done*), and it is OUTWARD-FACING — a real message to a real member. **D2's law is that
// every owner mutation is posted TWICE and the sentence describing it is composed by the SERVER**,
// because a confirm written on the client can describe something other than what the server then
// does. So the sentence lives here, beside the words it is about, and both sides read it.
//
// It is OWNER-facing, so it is deliberately NOT put through `touchViolations` — that gate is about
// what a MEMBER reads (no first person, no timeline, no machinery), and this sentence has to name
// the machinery she is operating.
export function closureConfirmSentence({ memberName = "", already = false } = {}) {
  const who = String(memberName || "").trim() || "this member";
  if (already) return `${who} has already had the closure note. It is sent once and only once.`;
  return `Send ${who} the closure note now? They will read: "${CLOSURE}"`;
}

// What the server says AFTER it acted, so her screen reports what really happened rather than what
// was requested (R66: an act that landed is not silent, and a refusal says so).
export function closureOutcome({ sent, memberName = "" } = {}) {
  const who = String(memberName || "").trim() || "They";
  return sent
    ? `${who} has been told the thing they wrote in about is fixed.`
    : `Nothing was sent. ${who} has already had the closure note, and it goes once.`;
}

// The gate on the copy itself, so a future edit cannot quietly break her rules. Run by the fixture
// against both strings above, which is what stops this being a comment nobody enforces.
export function touchViolations(text) {
  const s = String(text ?? "");
  const out = [];
  // NOT FAKE-LALA: no first-person singular, no signature.
  if (/\bI\b|\bI'?(?:m|ll|ve|d)\b|\bmy\b|— ?Lala|Lala here|love,?\s*Lala/i.test(s)) {
    out.push("speaks as a person; the automated touches are FROM THE HOUSE, never signed as Lala");
  }
  // NO TIMELINE PROMISES.
  if (/\b(?:within|in) \d+\s*(?:hours?|days?|weeks?|business days?)\b|\bby (?:tomorrow|monday|tuesday|wednesday|thursday|friday|the end of)\b|\bshortly\b|\bsoon\b|\bASAP\b/i.test(s)) {
    out.push("promises a timeline");
  }
  // NO MACHINERY TALK.
  if (/\b(?:ticket|queue|triage|classifier|runbook|engine|model|AI|automat(?:ed|ic)|system|backlog|sprint|deploy)\b/i.test(s)) {
    out.push("talks about the machinery");
  }
  if (/—/.test(s)) out.push("uses an em dash");
  return out;
}

// ---------------------------------------------------------------------------
// A1-5 — THE LEDGER. Entry types, shared with D4's service log so there is ONE shape.
//
// D4-2's order is "ONE TABLE, ONE SHAPE, APPEND-ONLY BY DATABASE RULE", and it says a future AI
// support layer "reads the history AS DATA and appends its own entries THROUGH THE SAME DOOR". A1 IS
// that layer, and D4 is not built yet — so A1 creates that table to D4's spec rather than a private
// one D4 would later have to merge. The `agent` type is A1's; the rest are declared now so D4 fills
// them in without a migration.
// ---------------------------------------------------------------------------
export const SERVICE_ENTRY_TYPES = [
  "agent",      // A1: a triage decision, draft, approval, rejection, or member touch
  "contact",    // the member wrote in
  "note",       // D4-1(b): her own note
  "refund",
  "grant",
  "violation",
  "probe",
  "fix",
  "ticket",     // ITEM 9: an IT ticket was filed about this member, from any surface
  // ITEM 4b (2026-08-13): the house ANSWERED her. It is an entry on this one append-only history
  // rather than a table of its own -- 055's own migration says a support layer appends THROUGH THE
  // SAME DOOR, and a second history of what this house has said to a member is the one that drifts
  // (R28/R29). Without this row on the roster `logService` refuses every reply SILENTLY, which is
  // the price ITEM 9 paid learning `TICKET_SOURCES` had to name `contact` first.
  "reply",
];
export const serviceTypeValid = (t) => SERVICE_ENTRY_TYPES.includes(String(t || ""));

// TS1(4) — WHAT EACH ENTRY TYPE IS CALLED WHERE SHE READS IT.
//
// Her clause 4 puts this history in the member's modal, so the types need words — and a label typed
// at the render site is a second answer key for what a row IS (R28/R29, and 14-B5's lesson that copy
// living in a TSX file is copy no gate can ask about). Held as a biconditional against the roster
// above, so a type added for D4 arrives with words or goes red.
//
// **AN UNROSTERED TYPE SHOWS ITS OWN KEY RATHER THAN A PLAUSIBLE LABEL** (R25's DrawerTab ruling): a
// visibly machine word tells her something is unaccounted for, where a nearest-fit word would tell
// her something false about a member's record.
export const SERVICE_ENTRY_LABELS = {
  agent: "Support",
  contact: "Wrote in",
  note: "Your note",
  refund: "Refund",
  grant: "Gift",
  violation: "Conduct",
  probe: "Probe",
  fix: "Fixed",
  ticket: "IT ticket",
  reply: "Answered",
};
export const serviceEntryLabel = (t) => {
  const key = String(t || "");
  // The ROSTER is asked before the lookup (R183): `SERVICE_ENTRY_LABELS["constructor"]` is a truthy
  // non-label, and a rendering that took it would print a function at her.
  return serviceTypeValid(key) ? SERVICE_ENTRY_LABELS[key] : key;
};

// The one-line summary each agent entry carries. Composed here so the ledger reads consistently
// whoever wrote the row.
//
// TS1(3)(a) — `repair` is the repair's OWN past-tense phrase, resolved by the caller from the
// `REPAIRS` roster and never typed at the call site (R28/R29): what a repair DID is a property of the
// repair, and a second wording of it in a route is how a ledger comes to disagree with the control
// that offered it. With none named the sentence says only what it can honestly say.
export function agentEntrySummary({ action, category, draftId = "", repair = "" } = {}) {
  const cat = CATEGORIES.find((c) => c.key === category)?.label || category || "";
  if (action === "repaired") {
    const what = String(repair || "").trim();
    return what ? `A known repair was performed: ${what}.` : "A known repair was performed.";
  }
  // TS2(1) — the support brain's own three, so a Lala-handled ticket reads in her ledger exactly as a
  // form-handled one does. Her clause 3 is ONE dashboard view regardless of door, and one shape is
  // what makes that possible (A1-5/D4-2: one table, one shape).
  if (action === "assisted") return "Lala checked their account and guided them. Nothing was wrong.";
  if (action === "filed") return "Lala found something real and it was filed for the team.";
  if (action === "escalated") return "Lala did not answer this one. It came to you.";
  if (action === "classified") return `Triaged as ${cat}.`;
  if (action === "drafted") return `Drafted an order from this message${draftId ? ` (${draftId})` : ""}.`;
  if (action === "approved") return "You approved the drafted order. It entered the runbook.";
  if (action === "rejected") return "You rejected the drafted order. Nothing entered the runbook.";
  if (action === "acknowledged") return "The house acknowledged the message.";
  if (action === "closed") return "The house told them the fix had shipped.";
  return `Triage: ${action}.`;
}
